import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FaRobot, FaPaperPlane, FaTimes, FaCommentDots } from "react-icons/fa";

const API_BASE_URL = process.env.REACT_APP_CHAT_API_URL || "";
const MAX_INPUT_LENGTH = 500;
const REQUEST_COOLDOWN_MS = 1000;

const starterMessage = {
  role: "assistant",
  content:
    "Hi, I am Yuhao's AI assistant. Ask me about his experience, projects, or skills.",
};

const ChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([starterMessage]);
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [lastSentAt, setLastSentAt] = useState(0);
  const inputRef = useRef(null);
  const panelRef = useRef(null);
  const closeButtonRef = useRef(null);
  const sendButtonRef = useRef(null);
  const messagesEndRef = useRef(null);

  const canSend = useMemo(() => {
    return (
      !isLoading &&
      inputValue.trim().length > 0 &&
      inputValue.trim().length <= MAX_INPUT_LENGTH
    );
  }, [inputValue, isLoading]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages]);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (!isOpen) {
        return;
      }

      if (event.key === "Escape") {
        setIsOpen(false);
        return;
      }

      if (event.key !== "Tab" || !panelRef.current) {
        return;
      }

      const focusableElements = [
        closeButtonRef.current,
        inputRef.current,
        sendButtonRef.current,
      ].filter(Boolean);

      if (focusableElements.length === 0) {
        return;
      }

      const first = focusableElements[0];
      const last = focusableElements[focusableElements.length - 1];
      const active = document.activeElement;

      if (event.shiftKey && active === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isOpen]);

  const sendMessage = async (event) => {
    event.preventDefault();

    const trimmed = inputValue.trim();
    if (!trimmed || trimmed.length > MAX_INPUT_LENGTH || isLoading) {
      return;
    }

    const now = Date.now();
    if (now - lastSentAt < REQUEST_COOLDOWN_MS) {
      setErrorMessage("Please wait a moment before sending another message.");
      return;
    }

    const userMessage = { role: "user", content: trimmed };
    const nextMessages = [...messages, userMessage];

    setInputValue("");
    setErrorMessage("");
    setIsLoading(true);
    setLastSentAt(now);
    setMessages(nextMessages);

    const timeoutController = new AbortController();
    const timeout = setTimeout(() => timeoutController.abort(), 15000);

    try {
      const response = await fetch(`${API_BASE_URL}/api/chat`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        signal: timeoutController.signal,
        body: JSON.stringify({
          messages: nextMessages,
        }),
      });

      const data = await response.json();
      if (!response.ok) {
        const message = [data?.error, data?.details].filter(Boolean).join(" ");
        throw new Error(message || "The assistant is unavailable right now.");
      }

      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content:
            data.reply ||
            "I could not generate an answer right now. Please try again.",
        },
      ]);
    } catch (error) {
      setErrorMessage(
        error.name === "AbortError"
          ? "Request timed out. Please try again."
          : error.message || "Something went wrong. Please try again."
      );
    } finally {
      clearTimeout(timeout);
      setIsLoading(false);
    }
  };

  return (
    <div className="chat-widget-root">
      <motion.button
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        className="chat-launcher"
        onClick={() => setIsOpen((prev) => !prev)}
        aria-label={isOpen ? "Close AI chat" : "Open AI chat"}
      >
        {isOpen ? <FaTimes /> : <FaCommentDots />}
      </motion.button>

      <AnimatePresence>
        {isOpen && (
          <motion.aside
            ref={panelRef}
            className="chat-panel"
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            role="dialog"
            aria-label="AI chat assistant"
          >
            <header className="chat-header">
              <div className="chat-header-title">
                <FaRobot />
                <span>Ask Yuhao AI</span>
              </div>
              <button
                ref={closeButtonRef}
                className="chat-close-button"
                onClick={() => setIsOpen(false)}
                aria-label="Close chat dialog"
              >
                <FaTimes />
              </button>
            </header>

            <div className="chat-messages" aria-live="polite">
              {messages.map((message, index) => (
                <div
                  key={`${message.role}-${index}`}
                  className={`chat-bubble ${message.role === "user" ? "user" : "assistant"}`}
                >
                  {message.content}
                </div>
              ))}
              {isLoading && (
                <div className="chat-bubble assistant">Thinking...</div>
              )}
              <div ref={messagesEndRef} />
            </div>

            <form className="chat-input-wrap" onSubmit={sendMessage}>
              <label className="sr-only" htmlFor="chat-message-input">
                Type your message
              </label>
              <input
                id="chat-message-input"
                ref={inputRef}
                className="chat-input"
                type="text"
                value={inputValue}
                onChange={(event) => setInputValue(event.target.value)}
                maxLength={MAX_INPUT_LENGTH}
                placeholder="Ask about projects, skills, or experience..."
                aria-label="Chat input"
              />
              <button
                ref={sendButtonRef}
                type="submit"
                className="chat-send-button"
                disabled={!canSend}
                aria-label="Send message"
              >
                <FaPaperPlane />
              </button>
            </form>
            <p className="chat-meta-text">
              {inputValue.length}/{MAX_INPUT_LENGTH}
            </p>
            {errorMessage && <p className="chat-error-text">{errorMessage}</p>}
          </motion.aside>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ChatWidget;
